import "../../../../assets/scss/custom-bootstrap.scss";
import "../../../../assets/scss/variables.scss";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Input } from "../../(components)/ui/input";
import { Eye, EyeOff, Lock } from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";
import WithAuthLayout from "../withAuthLayout";
import { initGA, pageView } from "../../../../utils/ga4";
import { useFormik } from "formik";
import * as Yup from "yup";

import { bypassCaptchaEmail } from "../../../../utils/validation";

import { loginAndPersistSession } from "../../../../utils/authApi";

import TermsAndConditions from "../../(components)/ui/TermsAndConditions";
import { buttonStyles, StyledButton } from "../../../../Shared/button/button";
import { testIdProps } from "../../../../utils/testIdProps";
import ToastWarning from "../../../(common)/Toast/ToastWarning";
import ToastInfo from "../../../(common)/Toast/ToastInfo";

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [passwordShown, setPasswordShown] = useState(false);
  const history = useNavigate();
  const measurementId: string = process.env.REACT_APP_MEASUREMENT_ID as string;
  let captcha: any;

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      agreeToTerms: true,
      recaptchaToken: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .required("Email cannot be empty")
        .email("Enter a valid email")
        .test(
          "starts-with-letter",
          "Email cannot start with a number",
          (value) => (value ? isNaN(Number(value[0])) : true)
        )
        .test("no-special-char-domain", "Enter a valid email", (value) => {
          if (!value) return false;
          const domain = value.split("@")[0];
          return !/[!#$%^&*()"'`~<>/?]/.test(domain);
        }),
      password: Yup.string().required("Password cannot be empty"),
      agreeToTerms: Yup.boolean().oneOf(
        [true],
        "Please accept terms to continue"
      ),
      recaptchaToken: Yup.string().when("email", {
        is: (email: string) => !bypassCaptchaEmail.includes(email),
        then: (schema) => schema.required("Captcha is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
    onSubmit: async (values) => {
      if (loading) return;
      await handleLogin(values);
    },
  });

  async function getOrgInfo(
    orgId: string | undefined,
    onboardingStatus?: string
  ) {
    if (!orgId) {
      setLoading(false);
      return;
    }

    localStorage.setItem("organisation_id", orgId);
    setLoading(false);
    history("/application/dashboard");
  }

  const setCaptchaRef = (ref: any) => {
    if (ref) {
      return (captcha = ref);
    }
  };

  async function handleLogin(values: any) {
    if (!values || !values.email || !values.password) {
      ToastInfo("error", ["Missing email or password"]);
      return;
    }

    const allCheckPassed =
      bypassCaptchaEmail.includes(values.email) || values.recaptchaToken;
    if (!allCheckPassed) {
      ToastInfo("Captcha Token not present", []);
      return;
    }

    setLoading(true);

    try {
      const data = await loginAndPersistSession({
        email: values.email,
        password: values.password,
        headers: {
          captchaToken: values.recaptchaToken?.toString() || "",
        },
      });

      if ((data as any)?.statusCode === 404) {
        localStorage.clear();
        ToastInfo("Please create an account ", (data as any)?.message);
        return history("/sign-up");
      }

      const orgId =
        data?.user?.organisation_id ||
        data?.user?.organisation_pid ||
        localStorage.getItem("organisation_id") ||
        undefined;

      if (orgId) {
        getOrgInfo(orgId, data?.user?.onboarding_status);
      } else {
        setLoading(false);
        history("/complete-organisation");
      }

      initGA(measurementId, {
        userId: orgId || "",
      });

      pageView("pageview", window.location.pathname);
    } catch (err: any) {
      captcha.reset();
      formik.setFieldValue("recaptchaToken", "");
      localStorage.setItem("email", values.email);

      setLoading(false);

      if (err?.statusCode === 404) {
        ToastInfo("Please create an account", err?.message);
        return history("/sign-up");
      }

      ToastWarning("", err?.message);
    }
    return;
  }

  return (
    <main className="d-flex align-items-center gap-5 justify-content-center">
      <form onSubmit={formik.handleSubmit}>
        <div
          className="container d-flex flex-column shadow bg-white border rounded-4 p-4"
          style={{ maxWidth: 400 }}
        >
          <p className="text-left fs-3 fw-semibold text-primary-100">Login</p>

          <div className="form-group position-relative mb-3">
            <Input
              id="login-email"
              type="email"
              name="email"
              className={`form-control bg-light`}
              placeholder="Enter Business Email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
            {formik.touched.email && formik.errors.email && (
              <div className="text-xs text-danger d-block">
                {formik.errors.email}
              </div>
            )}
          </div>

          <div className="form-group position-relative mb-3">
            <Input
              id="login-password"
              type={passwordShown ? "text" : "password"}
              name="password"
              className={`form-control `}
              placeholder="Password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              suffixIcon={
                <div
                  {...testIdProps("login-password-toggle")}
                  className="position-absolute d-flex align-items-center justify-content-center"
                  style={{
                    right: "2px",
                    cursor: "pointer",
                    height: "38px",
                    width: "38px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={() => setPasswordShown(!passwordShown)}
                >
                  {passwordShown ? (
                    <Eye
                      style={{ cursor: "pointer" }}
                      size={20}
                      color="#C4C4C4"
                    />
                  ) : (
                    <EyeOff
                      style={{ cursor: "pointer" }}
                      size={20}
                      color="#C4C4C4"
                    />
                  )}
                </div>
              }
            />
            {formik.touched.password && formik.errors.password && (
              <div className="text-xs text-danger d-block">
                {formik.errors.password}
              </div>
            )}

            <div className="d-flex justify-content-end mt-2">
              <a
                {...testIdProps("forgot-password-link")}
                href="/forgot-password"
                className="text-typo-titles-H2 text-xs text-right"
              >
                Forgot Password?
              </a>
            </div>
          </div>

          <div className="form-group mb-3">
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY!}
              onChange={(token) =>
                formik.setFieldValue("recaptchaToken", token)
              }
              ref={(r) => setCaptchaRef(r)}
              // size="normal"
            />
          </div>

          <TermsAndConditions formik={formik} />

          <StyledButton
            id="login-submit"
            type="submit"
            style={
              formik.isSubmitting || loading || !formik.isValid
                ? buttonStyles.disabled.normal
                : buttonStyles.primary.normal
            }
            hoverStyle={
              formik.isSubmitting || loading || !formik.isValid
                ? buttonStyles.disabled.hover
                : buttonStyles.primary.hover
            }
            disabled={
              !formik.isValid || !formik.dirty || formik.isSubmitting || loading
            }
            isLoading={loading}
          >
            {loading ? "Please wait..." : "Continue"}
          </StyledButton>

          <div className="text-center small text-muted mt-3 d-flex align-items-center justify-content-center gap-2">
            <Lock size={14} />
            Your data is secure with us
          </div>

          <div className="d-flex align-items-center my-3">
            <hr className="flex-grow-1" />
            <span className="mx-2 small text-muted">or</span>
            <hr className="flex-grow-1" />
          </div>

          <StyledButton
            className="text-typo-h1 rounded-2 text-base cursor-pointer bg-white border border-typo-h1 w-100 py-2"
            type="button"
            onClick={() => navigate("/sign-up")}
            id="create-account-link"
            style={buttonStyles.secondary.normal}
            hoverStyle={buttonStyles.secondary.hover}
          >
            Create an Account
          </StyledButton>
        </div>
      </form>
    </main>
  );
};

export default WithAuthLayout(Login);
